<script setup lang="ts">
import TheIcon from '@/Components/TheIcon.vue'
import { FilterActiveOptions } from '@/Pages/the-city/interfaces/Filters'
import ActiveFilters from '@/Pages/the-city/components/ActiveFilters.vue'
import { FilterGroup } from '@/Pages/the-city/interfaces/Filters'
import TheFilterMultiple from './TheFilterMultiple.vue'
import TheFilterSingle from './TheFilterSingle.vue'
import TheFilterRange from './TheFilterRange.vue'
import { ref, computed, Ref, watch, nextTick } from 'vue'
import { filterGroupType } from '../enums/Filters'
import { activeFilterOptions, setSendFilterRequest } from '@/Composables/use-listing-filter'

type Props = {
    activeFilters: FilterActiveOptions[],
    filterData: FilterGroup[] | undefined
    allowHide: boolean
    isLoading: boolean
    isMobile: boolean
    isFiltersShown: boolean,
}

const props = defineProps<Props>()
const emit = defineEmits(['closeFilters', 'resetAllFilters', 'resetFilter', 'open-filters', 'update:activeFilters'])
const filterRef: Ref<HTMLElement | null> = ref(null)

// Function to update active filters based on filterData
const updateActiveFilters = (): void => {
    if (!props.filterData) return

    const newActiveFilters: FilterActiveOptions[] = []

    props.filterData.forEach(filter => {
        if (Array.isArray(filter.options)) {
            // Handle checkbox and radio filters
            const checkedOptions = filter.options.filter(option => option.isChecked === true)

            checkedOptions.forEach(option => {
                newActiveFilters.push({
                    category: filter.category,
                    id: option.id,
                    title: option.title
                })
            })
        } else if (filter.selectType === 'rangePrice' && filter.options) {
            // Handle price range filters
            const { min, max, selectedValues } = filter.options

            // Skip if selectedValues are zero or not set
            if (selectedValues && selectedValues.min !== 0 && selectedValues.max !== 0 &&
                (selectedValues.min > min || selectedValues.max < max)) {
                newActiveFilters.push({
                    category: filter.category,
                    min: selectedValues.min,
                    max: selectedValues.max,
                    options: {
                        currencyInfo: filter.options.currencyInfo
                    }
                })
            }
        }
    })

    // Update the global filterOptions ref directly
    if (JSON.stringify(activeFilterOptions.value) !== JSON.stringify(newActiveFilters)) {
        activeFilterOptions.value = newActiveFilters
    }
}

const filteredData = computed(() => {
    return props.filterData?.map(filter => {
        if (Array.isArray(filter.options)) {

            const filteredOptions = filter.options.filter(option => option.count > 0)
            return {
                ...filter,
                options: filteredOptions
            }
        } else if (filter.selectType === 'rangePrice') {

            return filter
        }
    })
        .filter(filter => {
            if (Array.isArray(filter?.options)) {
                return filter.options.length > 0
            } else if (filter?.selectType === 'rangePrice') {
                return filter.options.min > 0 && filter.options.max > 0
            }

            return false
        })

        .sort((a, b) => {

            if (a?.selectType === 'rangePrice' || b?.selectType === 'rangePrice') {
                return 0
            }

            const aHasChecked = Array.isArray(a?.options) && a.options.some(option => option.isChecked)
            const bHasChecked = Array.isArray(b?.options) && b.options.some(option => option.isChecked)

            if (aHasChecked && !bHasChecked) {
                return -1
            } else if (!aHasChecked && bHasChecked) {
                return 1
            } else {
                return 0
            }
        })
})

watch(
    () => props.filterData,
    () => {
        setSendFilterRequest(false)  // Disable requests during manual update
        updateActiveFilters()

        // Re-enable normal behavior after update
        nextTick(() => {
            setSendFilterRequest(null)
        })
    },
    { once: true }
)

// Initialize active filters on component mount
// onMounted(() => {
//     updateActiveFilters()
// })
</script>

<template>
    <div
        ref="filterRef"
        class="hz-filters border border-primary rounded"
    >
        <transition name="blur">
            <div
                v-if="isLoading && !isMobile"
                class="blur-loader"
                :style="!props.isMobile && filterRef ? { height: filterRef.scrollHeight + 'px'} : {}"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                    <circle
                        fill="#FF5852"
                        stroke="#FF5852"
                        stroke-width="10"
                        r="15"
                        cx="40"
                        cy="65"
                    >
                        <animate
                            attributeName="cy"
                            calcMode="spline"
                            dur="2"
                            values="65;135;65;"
                            keySplines=".5 0 .5 1;.5 0 .5 1"
                            repeatCount="indefinite"
                            begin="-.4"
                        />
                    </circle>
                    <circle
                        fill="#FF5852"
                        stroke="#FF5852"
                        stroke-width="10"
                        r="15"
                        cx="100"
                        cy="65"
                    >
                        <animate
                            attributeName="cy"
                            calcMode="spline"
                            dur="2"
                            values="65;135;65;"
                            keySplines=".5 0 .5 1;.5 0 .5 1"
                            repeatCount="indefinite"
                            begin="-.2"
                        />
                    </circle>
                    <circle
                        fill="#FF5852"
                        stroke="#FF5852"
                        stroke-width="10"
                        r="15"
                        cx="160"
                        cy="65"
                    >
                        <animate
                            attributeName="cy"
                            calcMode="spline"
                            dur="2"
                            values="65;135;65;"
                            keySplines=".5 0 .5 1;.5 0 .5 1"
                            repeatCount="indefinite"
                            begin="0"
                        />
                    </circle>
                </svg>
                <!-- <TheIcon
                    icon-id="loader-icon"
                    width="30"
                    height="24"
                    class="d-flex"
                    stroke="#4A5268"
                /> -->
            </div>
        </transition>
        <div class="filters-header mb-2">
            <div class="filters-header-wrap">
                <button
                    v-if="isMobile"
                    :class="{'pe-none': isLoading}"
                    class="filters-header-icon-close"

                    @click="emit('closeFilters')"
                >
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M18 6L6 18"
                            stroke="#FF5852"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <path
                            d="M6 6L18 18"
                            stroke="#FF5852"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
                <div class="filters-header-inner">
                    <span class="filters-header-title">
                        Filters
                    </span>
                    <TheIcon
                        v-if="!allowHide && !isMobile"
                        class="mr-2 cursor-pointer"
                        icon-id="circle-arrow-left"
                        width="24"
                        height="24"
                        @click="emit('closeFilters')"
                    />
                    <button
                        v-if="isMobile"
                        class="filters-header-clear-btn "
                        :class="{'pe-none': isLoading}"
                        @click="emit('resetAllFilters')"
                    >
                        CLEAR
                    </button>
                </div>
            </div>
        </div>

        <ActiveFilters
            v-if="activeFilters?.length"
            :active-filters="activeFilters"
            @reset-all-filters="emit('resetAllFilters')"
            @reset-filter="emit('resetFilter', $event)"
        />

        <hr class="divider">
        <div v-for="filter in filteredData" :key="filter?.category" class="hz-filters__content">
            <TheFilterMultiple
                v-if="filter && Array.isArray(filter.options) && filter.selectType === filterGroupType.CHECKBOX && !allowHide"
                :filter-data="filter"
                :is-mobile="isMobile"
            />
            <TheFilterSingle
                v-if="filter && filter.selectType === filterGroupType.RADIO && !allowHide"
                :filter-data="filter"
                :is-mobile="isMobile"
            />
            <TheFilterRange
                v-if="filter && filter.selectType === filterGroupType.PRICE_RANGE && !allowHide"
                :filter-data="filter"
                :is-mobile="isMobile"
            />
        </div>

        <button
            v-if="isMobile"
            class="btn btn-primary tt-n show-results"
            :class="{'pe-none': isLoading}"
            @click="emit('closeFilters')"
        >
            <div>Show results</div>
            <div v-if="isLoading" class="location-suggestion">
                <div
                    class="d-flex justify-content-center spinner-wrap"
                >
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        </button>
    </div>
</template>

<style scoped lang="scss">
@import '@/Pages/the-city/mixins/filters';

.show-results {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    transition: all .3s ease;
}

.blur-enter-active,
.blur-leave-active {
    transition: all 0.5s ease;
}

.blur-enter-from,
.blur-leave-to {
    opacity: 0;
}

.blur-loader {
    position: absolute;
    top: 0;
    left: 0px;
    width: 100%;
    height: 100%;
    background: #00000026;
    z-index: 100;
    border-radius: 6px;
    display: flex;
    align-items: baseline;
    justify-content: center;

    svg {
        width: 20%;
    }
}

.hz-filters-wrapper.mobile .hz-filters {
    @supports not (height: 100dvh) {
        padding-bottom: 100px;
    }

    @media (min-width: 768px) {
        @supports not (height: 100dvh) {
            padding-bottom: 40px;
        }
    }

    @media (min-width: 992px) {
        @supports not (height: 100dvh) {
            padding-bottom: 0;
        }
    }
}

.hz-filters {
    position: relative;
    border: 1px solid var(--accent-bg);

    &__header {
        display: flex;
        justify-content: space-between;

        span {
            font-size: 24px;
            font-weight: 600;
            color: var(--heading-dark);
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
    }
}

.hz-filters-group {
    &__title {
        font-size: 16px;
        font-weight: 600;
        color: var(--main-text);

        span {
            font-size: 14px;
            font-weight: 400;
        }
    }

    &__items {
        font-size: 14px;
    }
}

.hz-price-range {
    display: flex;
    align-items: end;
    gap: 8px;

    &__dash {
        margin-bottom: 12px;
    }
}

.hz-price-input {
    label {
        @include label;
    }

    input {
        background-color: var(--light-bg);
        height: 46px;
        padding-left: 40px;
        font-size: 16px;
    }

    :deep(.hz-price-input__icon) {
        position: absolute;
        top: 50%;
        left: 6px;
        transform: translateY(-53%);
    }
}

.divider {
    border-top: 1px solid var(--accent-bg);
    margin: 16px 0;
}

.filters-header {
    justify-content: space-between;
    align-items: center;

    &-title {
        font-size: 24px;
        font-weight: 600;
        line-height: 36px;
        color: #4A5268;


        @media (max-width: 992px) {
            font-size: 20px;
            font-weight: 600;
            line-height: 22.4px;

        }

        @media (max-width: 768px) {
            font-size: 16px;
            font-weight: 600;
            line-height: 22.4px;
        }
    }

    &-icon-close {

        display: flex;
        align-items: center;
        justify-content: center;
        appearance: none;
        border: 0;
        outline: none;
        background-color: white;
        padding: 4px;


        svg {
            width: 24px;
            height: 24px;
            flex-shrink: 0;
        }

    }

    &-wrap {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 14px;

    }

    &-inner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
    }

    &-clear-btn {
        font-family: var(--font-family-poppins-sans-serif);
        font-size: 20px;
        font-weight: 700;
        line-height: 24px;
        letter-spacing: 0.03em;
        background-color: white;
        appearance: none;
        border: 0;
        outline: none;
        padding: 4px;
        color: #4A5268;

        @media (max-width: 768px) {
            font-size: 16px;
        }
    }
}
</style>
